import {
  TimeOvertimeQuantificationType,
  TimeOvertimeRoundingDirection,
  type TimeOvertimeTramo,
} from '@/generated/graphql/types';

import { parseHHMMToMinutes } from '../../utils';
import type {
  TCreateRequestFormErrors,
  TCreateRequestFormState,
} from './types';

export const REASON_MAX_LENGTH = 250;
const MAX_MINUTES_PER_DAY = 1440;
const MINUTES_PER_HOUR = 60;

// TIM-663 CAMBIO E — fit against the verified backend values in
// rounding-test-cases.md, not the doc's own prose formula: the doc itself
// says the two disagree for a mixed-direction tramos array (the FIFTEEN_MIN
// "P,N,P,N" row). Every tested value in that row rounds down, regardless of
// which tramo the formula's own index picks. A tramos array where every
// enabled entry shares one direction rounds uniformly with that direction —
// the only rule that reconciles every row in the document.
export const calculateRoundedOvertimeMinutes = (
  minutes: number,
  quantificationType: TimeOvertimeQuantificationType | null | undefined,
  tramos: ReadonlyArray<
    Pick<TimeOvertimeTramo, 'enabled' | 'rounding_direction'>
  >,
  blockMinutes: number | null | undefined,
): number => {
  if (
    !quantificationType ||
    quantificationType === TimeOvertimeQuantificationType.ExactTime
  ) {
    return minutes;
  }

  const width = blockMinutes ?? 0;
  const enabledTramos = tramos.filter(tramo => tramo.enabled);
  if (width <= 0 || enabledTramos.length === 0) {
    return minutes;
  }

  const coverage = width * enabledTramos.length;
  if (coverage < MINUTES_PER_HOUR) {
    return minutes;
  }

  const directions = new Set(
    enabledTramos.map(tramo => tramo.rounding_direction),
  );
  const uniformDirection =
    directions.size === 1 ? enabledTramos[0]?.rounding_direction : undefined;

  if (uniformDirection === TimeOvertimeRoundingDirection.Next) {
    return Math.ceil(minutes / width) * width;
  }
  // PREVIOUS, or a mixed-direction tramos array: round down to the block
  // width — verified against every row of rounding-test-cases.md.
  return Math.floor(minutes / width) * width;
};

// Empty text means "no time entered" — VAL-SOL-01 only needs one amount above
// zero, not that the field holds text.
export const parseAmountOrZero = (value: string): number | null => {
  if (!value.trim()) {
    return 0;
  }
  return parseHHMMToMinutes(value);
};

// TIM-663 CAMBIO A — the turno count reads `get_attendance_card.shifts`, a
// real list that can hold 0, 1 or 2+ entries. Guardar must not read a
// pending or failed query as "no turno": `shiftPending` blocks Guardar the
// same way, without claiming the day has no turno. A day with 2+ turnos
// blocks Guardar until one is picked (the multiturno selector, Julian
// 2026-09-22).
export type TShiftForDateStatus = {
  shifts: ReadonlyArray<{ id: string }> | undefined;
  selectedShiftId: string | null;
  loading: boolean;
  error: boolean;
};

export const validateCreateRequestForm = (
  state: TCreateRequestFormState,
  shiftStatus: TShiftForDateStatus,
): TCreateRequestFormErrors => {
  const errors: TCreateRequestFormErrors = {};

  if (!state.date) {
    errors.date = true;
  }
  if (state.date) {
    if (shiftStatus.loading || shiftStatus.error) {
      errors.shiftPending = true;
    } else if (!shiftStatus.shifts || shiftStatus.shifts.length === 0) {
      // CAMBIO 4 (Julian, 2026-09-20) — a day with no turno blocks Guardar.
      errors.shift = true;
    } else if (shiftStatus.shifts.length > 1 && !shiftStatus.selectedShiftId) {
      // CAMBIO A multiturno — 2+ turnos blocks Guardar until one is picked.
      errors.shift = true;
    }
  }

  const overtimeMinutes = parseAmountOrZero(state.overtimeText);
  const compensationMinutes = parseAmountOrZero(state.compensationText);

  if (overtimeMinutes === null) {
    errors.overtimeFormat = true;
  }
  if (compensationMinutes === null) {
    errors.compensationFormat = true;
  }

  if (overtimeMinutes === 0 && compensationMinutes === 0) {
    errors.overtimeRequired = true;
    errors.compensationRequired = true;
  }

  if (
    overtimeMinutes !== null &&
    compensationMinutes !== null &&
    overtimeMinutes + compensationMinutes > MAX_MINUTES_PER_DAY
  ) {
    errors.amountsExceedDay = true;
  }

  const trimmedReason = state.reason.trim();
  if (!trimmedReason) {
    errors.reason = true;
  } else if (trimmedReason.length > REASON_MAX_LENGTH) {
    errors.reasonMaxLength = true;
  }

  return errors;
};

export const hasFormErrors = (errors: TCreateRequestFormErrors): boolean =>
  Object.values(errors).some(Boolean);
